import { Box, Typography } from "@mui/joy";
import { useGigs } from "../../redux/gig/gigHooks";
import { useStages } from "../../redux/stage/stageHooks";
import { useProjects } from "../../redux/project/projectHooks";
import { useSeasons } from "../../redux/season/seasonHooks";
import { Stage } from "../../entities/stage";
import { Project } from "../../entities/project";
import { Season } from "../../entities/season";
import { Gig } from "../../entities/gig";
import { useMemo, useState } from "react";
import { ActionCard } from "./actionCard";
import { ServiceViewer } from "../servicesList/serviceViewer";
import { CircularProgress } from "@mui/joy";
import { compatibleSources } from "../../global";
import { useContractors } from "../../redux/contractor/contractorHooks";
import { Contractor } from "../../entities/contractor";

export type PendingAction = {
  project: Project;
  contractor: Contractor;
  season: Season;
  token: string;
  stage: Stage;
  gigs: Gig[];
};

/**
 *
 * @returns {ReactElement} ActionsRequired page
 */
export function ActionsRequired() {
  const { gigs, isLoading } = useGigs();
  const { stages } = useStages();
  const { projects } = useProjects();
  const { seasons } = useSeasons();
  const { contractors } = useContractors();
  const [viewer, setViewer] = useState<{ url: string; title?: string } | null>(
    null
  );

  function getActions() {
    const _actions: PendingAction[] = [];

    gigs?.forEach((gig) => {
      const isPastGig = new Date(gig.dateToUTC).getTime() < Date.now();
      if (isPastGig) return;

      const index = _actions.findIndex((a) => a.token == gig.publicToken);
      const stage = stages?.find((s) => gig.stageID === s.id);

      const project = projects?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.projectID
      );

      const contractor = contractors?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == gig.contractorID
      );

      const season = seasons?.find(
        (e) =>
          compatibleSources(e.sourceID, gig.sourceID) &&
          e.sourceRecordID == project?.seasonID
      );

      if (stage == null || (gig.interactors?.length ?? 0) == 0) return;
      if (index >= 0) {
        _actions[index].gigs.push(gig);
      } else {
        _actions.push({
          project: project,
          season: season,
          contractor: contractor,
          token: gig.publicToken,
          stage: stage,
          gigs: [gig],
        });
      }
    });

    return _actions;
  }

  const actions = getActions();
  return (
    <Box sx={{ pt: 1 }}>
      <Typography
        sx={{ fontWeight: 700, fontSize: "1.25rem", letterSpacing: "-0.01em" }}
        endDecorator={
          isLoading ? (
            <CircularProgress size="sm" />
          ) : (
            <Typography
              sx={{
                fontSize: "0.8125rem",
                fontWeight: 600,
                px: 1,
                py: 0.125,
                borderRadius: "999px",
                color: "var(--mui-palette-primary-main)",
                background:
                  "rgba(var(--mui-palette-primary-mainChannel) / 0.1)",
              }}
            >
              {actions?.length}
            </Typography>
          )
        }
      >
        Action{actions.length > 1 ? "s" : ""} Required
      </Typography>
      {!isLoading && actions.length === 0 ? (
        <Box
          sx={{
            mt: 1.5,
            p: 2,
            borderRadius: "16px",
            border: "1px dashed var(--mui-palette-divider)",
            display: "flex",
            alignItems: "center",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: 18, color: "#34C759" }}>
            <i className="fa-regular fa-circle-check"></i>
          </Typography>
          <Box>
            <Typography level="body2" sx={{ fontWeight: 600 }}>
              You're all caught up
            </Typography>
            <Typography
              level="body3"
              sx={{ color: "var(--mui-palette-text-secondary)" }}
            >
              No actions need your attention right now.
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            gap: 1.5,
            overflowX: "auto",
            pt: 1.5,
            pb: 1,
            width: "100%",
          }}
        >
          {actions.map((a) => (
            <ActionCard
              key={a.token}
              action={a}
              onOpen={(url, title) => setViewer({ url, title })}
            />
          ))}
        </Box>
      )}
      <ServiceViewer
        url={viewer?.url ?? null}
        title={viewer?.title}
        onClose={() => setViewer(null)}
      />
    </Box>
  );
}
